import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { RestHttpConfig } from "./rest-http.config";

export type RestHttpRouteValue = number | string;
export type RestHttpRoute = RestHttpRouteValue | RestHttpRouteValue[];

export type RestHttpParamValue = number | string | boolean;
export interface RestHttpParams {
  [key: string]: RestHttpParamValue | RestHttpParamValue[] | undefined;
}

export type RestHttpHeaders =
  | HttpHeaders
  | {
      [header: string]: string | string[];
    };

@Injectable()
export class RestHttp {
  constructor(protected config: RestHttpConfig, protected http: HttpClient) {}

  get baseUrl(): string {
    return this.config.baseUrl;
  }

  request(
    method: string,
    route: RestHttpRoute,
    body: any | null | undefined,
    params: RestHttpParams | undefined,
    responseType: "text",
    headers?: RestHttpHeaders
  ): Observable<string>;
  request<R>(
    method: string,
    route: RestHttpRoute,
    body?: any | null,
    params?: RestHttpParams,
    responseType?: "json",
    headers?: RestHttpHeaders
  ): Observable<R>;
  request(
    method: string,
    route: RestHttpRoute,
    body?: any | null,
    params?: RestHttpParams,
    responseType: "text" | "json" = "json",
    headers?: RestHttpHeaders
  ): Observable<any> {
    return this.http.request(method, this.getPath(route), {
      body,
      params: toHttpParams(params),
      responseType,
      headers
    });
  }

  get(
    route: RestHttpRoute,
    params: RestHttpParams | undefined,
    responseType: "text",
    headers?: RestHttpHeaders
  ): Observable<string>;
  get<R>(
    route: RestHttpRoute,
    params?: RestHttpParams,
    responseType?: "json",
    headers?: RestHttpHeaders
  ): Observable<R>;
  get(
    route: RestHttpRoute,
    params?: RestHttpParams,
    responseType: "text" | "json" = "json",
    headers?: RestHttpHeaders
  ): Observable<any> {
    // veracode 💩💩💩
    return this.http.get.call(this.http, this.getPath(route), {
      params: toHttpParams(params),
      responseType,
      headers
    } as any);
  }

  post<T>(
    route: RestHttpRoute,
    body: T,
    params: RestHttpParams | undefined,
    responseType: "text",
    headers?: RestHttpHeaders
  ): Observable<string>;
  post<T, R>(
    route: RestHttpRoute,
    body: T,
    params?: RestHttpParams,
    responseType?: "json",
    headers?: RestHttpHeaders
  ): Observable<R>;
  post(
    route: RestHttpRoute,
    body: any,
    params?: RestHttpParams,
    responseType: "text" | "json" = "json",
    headers?: RestHttpHeaders
  ): Observable<any> {
    return this.http.post(this.getPath(route), body, {
      params: toHttpParams(params),
      responseType,
      headers
    } as any);
  }

  put<T>(
    route: RestHttpRoute,
    body: T,
    params: RestHttpParams | undefined,
    responseType: "text",
    headers?: RestHttpHeaders
  ): Observable<string>;
  put<T, R>(
    route: RestHttpRoute,
    body: T,
    params?: RestHttpParams,
    responseType?: "json",
    headers?: RestHttpHeaders
  ): Observable<R>;
  put(
    route: RestHttpRoute,
    body: any,
    params?: RestHttpParams,
    responseType: "text" | "json" = "json",
    headers?: RestHttpHeaders
  ): Observable<any> {
    return this.http.put(this.getPath(route), body, {
      params: toHttpParams(params),
      responseType,
      headers
    } as any);
  }

  patch<T>(
    route: RestHttpRoute,
    body: T,
    params: RestHttpParams | undefined,
    responseType: "text",
    headers?: RestHttpHeaders
  ): Observable<string>;
  patch<T, R>(
    route: RestHttpRoute,
    body: T,
    params?: RestHttpParams,
    responseType?: "json",
    headers?: RestHttpHeaders
  ): Observable<R>;
  patch(
    route: RestHttpRoute,
    body: any,
    params?: RestHttpParams,
    responseType: "text" | "json" = "json",
    headers?: RestHttpHeaders
  ): Observable<any> {
    return this.http.patch(this.getPath(route), body, {
      params: toHttpParams(params),
      responseType,
      headers
    } as any);
  }

  delete(
    route: RestHttpRoute,
    params: RestHttpParams | undefined,
    responseType: "text",
    headers?: RestHttpHeaders
  ): Observable<string>;
  delete<R>(
    route: RestHttpRoute,
    params?: RestHttpParams,
    responseType?: "json",
    headers?: RestHttpHeaders
  ): Observable<R>;
  delete(
    route: RestHttpRoute,
    params?: RestHttpParams,
    responseType: "text" | "json" = "json",
    headers?: RestHttpHeaders
  ): Observable<any> {
    return this.http.delete(this.getPath(route), {
      params: toHttpParams(params),
      responseType,
      headers
    } as any);
  }

  head(
    route: RestHttpRoute,
    params: RestHttpParams | undefined,
    responseType: "text",
    headers?: RestHttpHeaders
  ): Observable<string>;
  head<R>(
    route: RestHttpRoute,
    params?: RestHttpParams,
    responseType?: "json",
    headers?: RestHttpHeaders
  ): Observable<R>;
  head(
    route: RestHttpRoute,
    params?: RestHttpParams,
    responseType: "text" | "json" = "json",
    headers?: RestHttpHeaders
  ): Observable<any> {
    return this.http.head(this.getPath(route), {
      params: toHttpParams(params),
      responseType,
      headers
    } as any);
  }

  options(
    route: RestHttpRoute,
    params: RestHttpParams | undefined,
    responseType: "text",
    headers?: RestHttpHeaders
  ): Observable<string>;
  options<R>(
    route: RestHttpRoute,
    params?: RestHttpParams,
    responseType?: "json",
    headers?: RestHttpHeaders
  ): Observable<R>;
  options(
    route: RestHttpRoute,
    params?: RestHttpParams,
    responseType: "text" | "json" = "json",
    headers?: RestHttpHeaders
  ): Observable<any> {
    return this.http.options(this.getPath(route), {
      params: toHttpParams(params),
      responseType,
      headers
    } as any);
  }

  getPath(route: RestHttpRoute): string {
    return getPath(this.baseUrl, route);
  }
}

const ROUTE_TRIM_REGEXP: RegExp = /^\/*(.*?)\/*$/;
export function getPath(baseUrl: string, route: RestHttpRoute): string {
  if (Array.isArray(route)) {
    route = route.join("/");
  } else {
    route = String(route);
  }

  if (route.startsWith("http://") || route.startsWith("https://")) {
    return route;
  }

  // trim starting and ending slashes (unnecessary)
  route = route.replace(ROUTE_TRIM_REGEXP, "$1");

  return baseUrl + "/" + route;
}

export function toHttpParams(params?: RestHttpParams): HttpParams | undefined {
  if (params == null) {
    return undefined;
  }

  return Object.keys(params).reduce((memo, key) => {
    const value:
      | RestHttpParamValue
      | RestHttpParamValue[]
      | undefined = params![key];
    if (Array.isArray(value)) {
      return value.reduce((prev, val) => {
        return prev.append(key, String(val));
      }, memo);
    } else if (value != null) {
      return memo.set(key, String(value));
    }
    return memo;
  }, new HttpParams());
}
